import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { AlertCircle, Loader, CheckCircle, Clock, AlertTriangle, ClipboardList } from 'lucide-react';
import { usePermissions } from '../context/PermissionsContext';

const getTimeElapsed = (dateStr) => {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return { text: `${days}d ${remHours}h`, isOverdue: true };
  }
  return { text: `${hours}h ${minutes}m`, isOverdue: false };
};

const StatCard = ({ title, value, color }) => (
  <div className={`bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 border-l-4 ${color.replace('bg-', 'border-l-')}`}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1 truncate">{title}</p>
    <h3 className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{value}</h3>
  </div>
);

const TechnicianDashboard = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/complaints/?page_size=9999');
        setComplaints(res.data.results || []);
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const openComplaints = complaints.filter(c => c.status === 'open').length;
  const inProgress = complaints.filter(c => c.status === 'in_progress').length;
  const resolvedToday = complaints.filter(c => {
    if (c.status !== 'resolved') return false;
    const ts = c.resolved_at || c.updated_at;
    if (!ts) return false;
    const today = new Date().toLocaleDateString('en-CA');
    return ts.startsWith(today);
  }).length;
  const criticalComplaints = complaints.filter(c => c.priority === 'critical' && c.status !== 'resolved' && c.status !== 'closed').length;

  const recentComplaints = complaints.slice(0, 5);
  const perms = usePermissions();
  const can = (key) => perms[key] !== false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Technician Dashboard</h1>
        <p className="text-sm text-gray-500">Overview of your assigned complaints</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Stats Grid */}
      {can('show_complaints') && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard title="Open" value={openComplaints} color="bg-blue-500" />
          <StatCard title="In Progress" value={inProgress} color="bg-yellow-500" />
          <StatCard title="Resolved Today" value={resolvedToday} color="bg-green-500" />
          <StatCard title="Critical" value={criticalComplaints} color="bg-red-500" />
        </div>
      )}

      {/* Recent Complaints */}
      {can('show_complaints') && <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <ClipboardList size={18} className="text-gray-400" />
          <h2 className="font-semibold text-gray-900">Recent Complaints</h2>
        </div>
        {recentComplaints.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle size={48} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400">No complaints assigned</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentComplaints.map(c => {
              const time = getTimeElapsed(c.created_at);
              const statusColors = {
                open: 'bg-blue-100 text-blue-700',
                in_progress: 'bg-yellow-100 text-yellow-700',
                resolved: 'bg-green-100 text-green-700',
                closed: 'bg-gray-100 text-gray-700',
              };
              const priorityColors = {
                low: 'bg-gray-100 text-gray-600',
                medium: 'bg-blue-100 text-blue-600',
                high: 'bg-orange-100 text-orange-600',
                critical: 'bg-red-100 text-red-600',
              };
              return (
                <div key={c.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 text-sm truncate">{c.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[c.status]}`}>
                        {c.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[c.priority]}`}>
                        {c.priority}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{c.customer_name} - {c.area_name || 'N/A'}</p>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${time.isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                    {time.isOverdue && <AlertTriangle size={12} />}
                    <Clock size={12} />
                    {time.text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {!can('show_complaints') && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-400">
          <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Complaints access has been restricted by the administrator.</p>
        </div>
      )}
    </div>
  );
};

export default TechnicianDashboard;
